"use client";

import Image from "next/image";
import { useEffect } from "react";

export default function Error({ error, reset }) {
  useEffect(() => {
    // Enhanced error logging for server components
    const logServerError = async () => {
      try {
        const errorData = {
          message: error.message || "Unknown error",
          stack: error.stack || "No stack trace available",
          digest: error.digest || "No digest available",
          name: error.name || "Error",
          cause: error.cause || null,
          componentStack: error.componentStack || null,
          errorBoundary: error.errorBoundary || null,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
        };

        // Send to our server error logging endpoint
        await fetch("/api/server-error-log", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(errorData),
        });
      } catch (logError) {
        console.error("❌ Failed to log server error:", logError);
      }
    };

    // Log the server error first
    logServerError();

    // Send error notification to Slack webhook
    // DISABLED: Only signup notifications are enabled
    const sendSlackNotification = async () => {
      console.log("ℹ️ Skipping Slack notification for error (notifications disabled except signups)");
      return;
      try {
        // Get user information from various sources
        let userInfo = "Not authenticated";
        let userEmail = "N/A";
        let userId = "N/A";

        try {
          // Try to get user from localStorage (correct Zustand persist key)
          const qensaStore = localStorage.getItem("qensa-store");

          if (qensaStore) {
            const parsedStore = JSON.parse(qensaStore);

            if (parsedStore?.state?.session?.user) {
              const user = parsedStore.state.session.user;
              userEmail = user.email || "N/A";
              userId = user.id || "N/A";
              userInfo = `${userEmail} (ID: ${userId})`;
            } else if (parsedStore?.state?.session) {
              userInfo = "Authenticated (session exists)";
            }
          }

          // Try alternative auth storage locations
          if (userInfo === "Not authenticated") {
            const authStorage = localStorage.getItem("auth-storage");

            if (authStorage) {
              const parsedAuth = JSON.parse(authStorage);
              if (parsedAuth?.state?.user) {
                const user = parsedAuth.state.user;
                userEmail = user.email || "N/A";
                userId = user.id || "N/A";
                userInfo = `${userEmail} (ID: ${userId})`;
              }
            }
          }
        } catch (authError) {
          // If localStorage parsing fails, try other methods
          try {
            // Try to get from Supabase session cookie or other auth indicators
            const cookies = document.cookie;
            if (cookies.includes("sb-") || cookies.includes("supabase")) {
              userInfo = "Authenticated (session detected via cookies)";
            }
          } catch (cookieError) {}
        }

        const errorDetails = {
          message: error?.message || "Unknown error",
          name: error?.name || "Error",
          stack: error?.stack || "No stack trace available",
          cause: error?.cause || null,
          componentStack: error?.componentStack || null,
          errorBoundary: error?.errorBoundary || null,
        };

        const errorMessage = {
          text: "🚨 Frontend Error Occurred",
          blocks: [
            {
              type: "header",
              text: {
                type: "plain_text",
                text: "🚨 Frontend Error Alert",
              },
            },
            {
              type: "section",
              fields: [
                {
                  type: "mrkdwn",
                  text: `*User:*\n${userInfo}`,
                },
                {
                  type: "mrkdwn",
                  text: `*Time:*\n${browserInfo.timestamp}`,
                },
              ],
            },
            {
              type: "section",
              text: {
                type: "mrkdwn",
                text: `*Error Details:*\n*Type:* ${errorDetails.name}\n*Message:* ${errorDetails.message}`,
              },
            },
            {
              type: "section",
              fields: [
                {
                  type: "mrkdwn",
                  text: `*URL:*\n${browserInfo.url}`,
                },
                {
                  type: "mrkdwn",
                  text: `*Referrer:*\n${browserInfo.referrer}`,
                },
              ],
            },
            {
              type: "section",
              text: {
                type: "mrkdwn",
                text: `*Stack Trace:*\n\`\`\`${errorDetails.stack.slice(0, 800)}\`\`\``,
              },
            },
            {
              type: "section",
              fields: [
                {
                  type: "mrkdwn",
                  text: `*Browser:*\n${browserInfo.userAgent.slice(0, 100)}...`,
                },
                {
                  type: "mrkdwn",
                  text: `*Viewport:*\n${browserInfo.viewport}`,
                },
              ],
            },
          ],
        };

        // Add component stack if available (React error boundary info)
        if (errorDetails.componentStack) {
          errorMessage.blocks.push({
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*Component Stack:*\n\`\`\`${errorDetails.componentStack.slice(0, 500)}\`\`\``,
            },
          });
        }

        const response = await fetch("/api/error-notification", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(errorMessage),
        });

        if (!response.ok) {
          const responseData = await response.json();
        } else {
          const responseData = await response.json();
        }
      } catch (slackError) {}
    };

    sendSlackNotification();
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center bg-gradient-to-b from-red-700 to-gray-900">
      <Image
        src="/logos/beta/white.svg"
        alt="Qensa Logo"
        width={180}
        height={60}
        className="mb-8"
      />

      <h1 className="text-4xl font-bold mb-4 text-white">
        Oops! Something went wrong
      </h1>

      <div className="bg-white/20 rounded-lg p-6 mb-6 max-w-lg backdrop-blur-sm">
        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-4 mx-auto">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h3 className="text-xl font-semibold text-white mb-2">We're on it!</h3>

        <p className="text-white/80 mb-4">
          Our team has been automatically notified of this issue and we're
          working to resolve it quickly.
        </p>

        <p className="text-white/70 text-sm mb-4">
          Need immediate help? Contact our support team:
        </p>

        <a
          href="mailto:contact@qensa.com"
          className="inline-block text-white font-medium hover:text-white/90 underline mb-4"
        >
          contact@qensa.com
        </a>
      </div>

      <div className="space-y-4">
        <button
          onClick={() => reset()}
          className="inline-block px-8 py-4 rounded-lg font-semibold bg-white text-red-700 hover:bg-white/90 transition-all duration-300"
        >
          Try Again
        </button>

        <p className="text-white/60 text-sm">
          <button
            onClick={() => (window.location.href = "/")}
            className="text-white underline hover:text-white/90"
          >
            Return to Home
          </button>
        </p>
      </div>
    </div>
  );
}
